import React, { useState, useEffect } from 'react';
import { Table, Button, Tag, Popconfirm, Space, Input, Modal, Form, Typography, Switch, InputNumber, Select, message } from 'antd';
import { AppstoreOutlined, PlusOutlined, EditOutlined, DeleteOutlined, CloudUploadOutlined, EyeOutlined } from '@ant-design/icons';
import api from '../api';

const { Title, Text } = Typography;

const statusMap = {
  enabled: { label: '已启用', color: 'green' },
  disabled: { label: '已禁用', color: 'default' },
  gray: { label: '灰度中', color: 'orange' },
};

function PluginManagePage() {
  const [plugins, setPlugins] = useState([]);
  const [loading, setLoading] = useState(false);
  const [createModal, setCreateModal] = useState(false);
  const [editModal, setEditModal] = useState(false);
  const [editGray, setEditGray] = useState(false);
  const [selected, setSelected] = useState(null);
  const [form] = Form.useForm();
  const [grayForm] = Form.useForm();

  const loadPlugins = async () => {
    setLoading(true);
    try {
      const { list } = await api.get('/admin/plugins', { params: { limit: 100 } });
      setPlugins(list);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadPlugins(); }, []);

  const handleCreate = async () => {
    try {
      const values = await form.validateFields();
      await api.post('/admin/plugins', values);
      message.success('插件创建成功');
      setCreateModal(false);
      form.resetFields();
      loadPlugins();
    } catch (e) {
      message.error(e.errorFields ? '请填写完整' : '创建失败');
    }
  };

  const handleStatusToggle = async (id, currentStatus) => {
    const newStatus = currentStatus === 'enabled' ? 'disabled' : 'enabled';
    await api.put(`/admin/plugins/${id}`, { status: newStatus });
    message.success(newStatus === 'enabled' ? '已启用' : '已禁用');
    loadPlugins();
  };

  const handleDelete = async (id) => {
    await api.delete(`/admin/plugins/${id}`);
    message.success('已删除');
    loadPlugins();
  };

  const handleEdit = (record) => {
    setSelected(record);
    form.setFieldsValue(record);
    setEditModal(true);
  };

  const handleEditSave = async () => {
    const values = form.getFieldsValue();
    await api.put(`/admin/plugins/${selected.id}`, values);
    message.success('保存成功');
    setEditModal(false);
    loadPlugins();
  };

  const handleGrayConfig = (record) => {
    setSelected(record);
    try {
      const grayUsers = record.gray_users ? JSON.parse(record.gray_users) : [];
      grayForm.setFieldsValue({ grayUsers: grayUsers.join(',') });
    } catch {
      grayForm.setFieldsValue({ grayUsers: '' });
    }
    setEditGray(true);
  };

  const handleGraySave = async () => {
    const { grayUsers } = grayForm.getFieldsValue();
    const arr = grayUsers ? grayUsers.split(',').map(s => parseInt(s.trim())).filter(Boolean) : [];
    await api.put(`/admin/plugins/${selected.id}`, { status: 'gray', grayUsers: JSON.stringify(arr) });
    message.success('灰度配置已更新');
    setEditGray(false);
    loadPlugins();
  };

  const columns = [
    { title: 'ID', dataIndex: 'id', width: 60 },
    {
      title: '插件标识', width: 140,
      render: (_, r) => <Tag color="blue" style={{ fontFamily: 'monospace' }}>{r.identifier}</Tag>,
    },
    { title: '名称', dataIndex: 'name', width: 120 },
    { title: '版本', dataIndex: 'version', width: 80 },
    { title: '描述', dataIndex: 'description', ellipsis: true },
    { title: '大小', width: 80, dataIndex: 'file_size', render: (v) => v ? `${(v / 1024).toFixed(1)}KB` : '-' },
    {
      title: '状态', width: 100,
      render: (_, r) => <Tag color={statusMap[r.status]?.color}>{statusMap[r.status]?.label || r.status}</Tag>,
    },
    {
      title: '操作', width: 260,
      render: (_, r) => (
        <Space size="small">
          <Switch size="small" checked={r.status === 'enabled'} onChange={() => handleStatusToggle(r.id, r.status)} checkedChildren="开" unCheckedChildren="关" />
          <Button size="small" icon={<EyeOutlined />} onClick={() => handleGrayConfig(r)}>灰度</Button>
          <Button size="small" icon={<EditOutlined />} onClick={() => handleEdit(r)}>编辑</Button>
          <Popconfirm title="确定删除?" onConfirm={() => handleDelete(r.id)}>
            <Button size="small" danger icon={<DeleteOutlined />} />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <Title level={4}>插件管理</Title>
        <Button type="primary" icon={<PlusOutlined />} onClick={() => setCreateModal(true)} style={{ background: '#07C160' }}>新增插件</Button>
      </div>

      <Table columns={columns} dataSource={plugins} rowKey="id" loading={loading} size="small" pagination={{ pageSize: 20 }} />

      {/* 创建插件 */}
      <Modal title="新增插件" open={createModal} onOk={handleCreate} onCancel={() => setCreateModal(false)}>
        <Form form={form} layout="vertical">
          <Form.Item name="identifier" label="插件标识" rules={[{ required: true, message: '如: plugin_chat' }]}>
            <Input placeholder="plugin_xxx" />
          </Form.Item>
          <Form.Item name="name" label="插件名称" rules={[{ required: true }]}>
            <Input placeholder="聊天窗口" />
          </Form.Item>
          <Form.Item name="version" label="版本号" rules={[{ required: true }]}>
            <Input placeholder="1.0.0" />
          </Form.Item>
          <Form.Item name="description" label="描述">
            <Input.TextArea rows={2} />
          </Form.Item>
          <Form.Item name="downloadUrl" label="下载地址">
            <Input placeholder="https://..." />
          </Form.Item>
          <Form.Item name="fileSize" label="文件大小(bytes)">
            <InputNumber style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item name="status" label="状态" initialValue="disabled">
            <Select options={[{ value: 'disabled', label: '禁用' }, { value: 'enabled', label: '启用' }, { value: 'gray', label: '灰度' }]} />
          </Form.Item>
        </Form>
      </Modal>

      {/* 编辑插件 */}
      <Modal title={`编辑插件 - ${selected?.name || ''}`} open={editModal} onOk={handleEditSave} onCancel={() => setEditModal(false)}>
        <Form form={form} layout="vertical">
          <Form.Item name="name" label="名称"><Input /></Form.Item>
          <Form.Item name="version" label="版本"><Input /></Form.Item>
          <Form.Item name="description" label="描述"><Input.TextArea rows={2} /></Form.Item>
          <Form.Item name="downloadUrl" label="下载地址"><Input /></Form.Item>
          <Form.Item name="fileSize" label="文件大小(bytes)"><InputNumber style={{ width: '100%' }} /></Form.Item>
        </Form>
      </Modal>

      {/* 灰度配置 */}
      <Modal title={`灰度配置 - ${selected?.name || ''}`} open={editGray} onOk={handleGraySave} onCancel={() => setEditGray(false)}>
        <Form form={grayForm} layout="vertical">
          <Form.Item name="grayUsers" label="灰度用户ID">
            <Input.TextArea rows={4} placeholder="用户ID列表，逗号分隔，如: 1,2,3,5,8" />
          </Form.Item>
          <Text type="secondary">配置后插件状态将变为"灰度"，仅这些用户可见该插件</Text>
        </Form>
      </Modal>
    </>
  );
}

export default PluginManagePage;
